import type { FC } from "react";
import React from "react";
import {
  dateToCustomFormatString,
  getHalfHourRange,
  getTimestampSettlementPeriod,
} from "@arenko-group/arenko-ui/dist/helpers";
import { arenkoChartServices } from "@arenko-group/arenko-ui/dist/theme/ArenkoTheme";
import LockIcon from "@mui/icons-material/Lock";
import { Divider, styled } from "@mui/material";
import { intervalToDuration } from "date-fns";
import { THIRTY_MINUTES_IN_MILLISECONDS } from "@/constants/date.constants";
import { getEnergyFromPower } from "@/helpers/common.helpers";
import type { TransformedWholesaleObj } from "@/pages/Energy";
import type { DateRange } from "@/types/common";

const StyledDivider = styled(Divider)(() => ({ marginBottom: 5 }));

const getRangeText = (from: string | number, to: string | number, days = 0) => {
  if (!from || !to) {
    return "";
  }

  const fromStr = dateToCustomFormatString(
    new Date(from),
    days === 0 ? "HH:mm" : "dd/MM HH:mm"
  );
  const toStr = dateToCustomFormatString(new Date(to), "HH:mm");
  return `${fromStr} - ${toStr}`;
};

type TooltipProps = {
  point: Highcharts.Point;
  marketVolumeData: TransformedWholesaleObj;
  selectedRange: DateRange;
  settlementPeriodInMilliseconds: number;
  scheduledData?: number;
  scheduledDisabledRange?: DateRange<number>[];
  maxContractablePower?: number;
  minContractablePower?: number;
};
const Tooltip: FC<TooltipProps> = ({
  point,
  marketVolumeData,
  selectedRange,
  settlementPeriodInMilliseconds,
  scheduledData,
  scheduledDisabledRange,
  maxContractablePower,
  minContractablePower,
}) => {
  const { days } = intervalToDuration({
    start: new Date(selectedRange.from),
    end: new Date(selectedRange.to),
  });
  //TODO: it might not always be half hour. Refactor if we start using different SP
  const [from, to] = getHalfHourRange(point.x);

  const scheduledEnergy = getEnergyFromPower(
    scheduledData ?? 0,
    point.x,
    point.x + settlementPeriodInMilliseconds
  );
  const scheduledPower = scheduledData;

  const contractedEnergy = getEnergyFromPower(
    point.y ?? 0,
    point.x,
    point.x + settlementPeriodInMilliseconds
  );

  const maxContractableEnergy = getEnergyFromPower(
    maxContractablePower ?? 0,
    point.x,
    point.x + settlementPeriodInMilliseconds
  );

  const minContractableEnergy = getEnergyFromPower(
    minContractablePower ?? 0,
    point.x,
    point.x + settlementPeriodInMilliseconds
  );

  const contractedPower = point.y ?? 0;
  return (
    <div data-testid="highchart-tooltip">
      <div data-testid="highchart-tooltip-title" style={{ margin: "0 0 3px" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {/* TODO: modify function to take sp length into consideration. Also SP might not be the standard text.  Refactor if we start using different SP */}
          <b>SP {getTimestampSettlementPeriod(point.x)}</b>{" "}
          <span style={{ marginLeft: 5 }}>{getRangeText(from, to, days)}</span>
        </div>
      </div>
      <StyledDivider light={true} />
      <div data-testid="highchart-tooltip-content">
        {scheduledDisabledRange ? (
          <>
            <div style={{ display: "flex" }}>
              <LockIcon style={{ transform: "scale(0.5)" }} />
              <div>
                <div>
                  <b>Power Profile Automation:</b>
                </div>
                <>
                  {scheduledDisabledRange.map(({ from, to }) => (
                    <div key={`${from}-${to}`}>
                      disabled&nbsp; {getRangeText(from, to)}
                    </div>
                  ))}
                </>
              </div>
            </div>
            <br />
          </>
        ) : null}
        <div>
          <span>
            Maximum Energy: {Math.abs(maxContractableEnergy).toFixed(1)}{" "}
            <b>MWh</b>, {Math.abs(maxContractablePower ?? 0).toFixed(1)}{" "}
            <b>MW</b>
          </span>
        </div>
        <div>
          <span>
            Minimum Energy: {Math.abs(minContractableEnergy).toFixed(1)}{" "}
            <b>MWh</b>, {Math.abs(minContractablePower ?? 0).toFixed(1)}{" "}
            <b>MW</b>
          </span>
        </div>
        <br />
        <div>
          <span>
            Total contracted:{" "}
            {/* TODO: update thirtyMins to the settlement period state */}
            {contractedEnergy.toFixed(1)} <b>MWh</b>,{" "}
            {contractedPower.toFixed(1)} <b>MW</b>
          </span>
        </div>
        {scheduledData !== undefined ? (
          <div>
            <span>
              Total scheduled:{" "}
              {/* TODO: update thirtyMins to the settlement period state */}
              {scheduledEnergy.toFixed(1)} <b>MWh</b>,{" "}
              {scheduledPower?.toFixed(1)} <b>MW</b>
            </span>
          </div>
        ) : null}
        <div>
          <span>
            Difference:{" "}
            {Math.abs(contractedEnergy - scheduledEnergy).toFixed(1)} <b>MWh</b>
            , {Math.abs(contractedPower - (scheduledPower ?? 0)).toFixed(1)}{" "}
            <b>MW</b>
          </span>
        </div>
        <br />
        <div>
          <span>Energy contracted:</span>
          <div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              <li data-testid="highchart-tooltip-item" style={{ margin: 0 }}>
                {/* TODO: Get this into a common styled span component */}
                <span
                  style={{
                    color: arenkoChartServices.imbalance.borderColor,
                    fontSize: 12,
                    marginRight: 5,
                    textShadow:
                      "-1px 0 white, 0 1px white, 1px 0 white, 0 -1px white",
                  }}
                >
                  ●
                </span>
                Imbalance:{" "}
                {getEnergyFromPower(
                  marketVolumeData.Imbalance,
                  point.x,
                  point.x + THIRTY_MINUTES_IN_MILLISECONDS
                ).toFixed(1)}{" "}
                MWh, {marketVolumeData.Imbalance.toFixed(1)} MW
              </li>
              <li data-testid="highchart-tooltip-item" style={{ margin: 0 }}>
                <span
                  style={{
                    color: arenkoChartServices.wholesale.borderColor,
                    fontSize: 12,
                    marginRight: 5,
                    textShadow:
                      "-1px 0 white, 0 1px white, 1px 0 white, 0 -1px white",
                  }}
                >
                  ●
                </span>
                Wholesale:{" "}
                {getEnergyFromPower(
                  marketVolumeData.Wholesale,
                  point.x,
                  point.x + THIRTY_MINUTES_IN_MILLISECONDS
                ).toFixed(1)}{" "}
                MWh, {marketVolumeData.Wholesale.toFixed(1)} MW
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tooltip;
